/**
 * ViewAsTenantCard — Property Manager tool for previewing what a specific
 * resident sees inside their app.
 *
 * Backend calls:
 *   GET /api/property-manager/tenants           → list of residents on the PM's property
 *   POST /api/pm/impersonate-tenant/{userId}    → starts a 30-min read-only session
 *
 * Security notes:
 *   - The impersonation session is marked read_only=True on the server
 *     side; the frontend also renders the top-of-app banner so the PM
 *     always knows they can't change financial data or passwords.
 *   - "End preview" restores the original PM cookie in a single tap.
 */
import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Eye, ShieldOff, User as UserIcon, Search } from 'lucide-react';
import { toast } from 'sonner';
import ResidentRewardBadge from './ResidentRewardBadge';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ViewAsTenantCard({ property }) {
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);

  useEffect(() => {
    if (!property?.property_id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/property-manager/tenants`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (alive) setTenants(Array.isArray(data) ? data : (data.tenants || []));
        }
      } catch (_e) { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [property?.property_id]);

  const impersonate = async (t) => {
    if (!window.confirm(`View ${t.name || t.email}'s tenant app?\n\nYou will enter a 30-minute READ-ONLY session. You cannot change passwords, make payments, or delete data while in this preview.`)) return;
    setSwitchingTo(t.user_id);
    try {
      const res = await fetch(`${API}/api/pm/impersonate-tenant/${t.user_id}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Preview failed'); return; }
      toast.success(`Now viewing as ${data.tenant?.name || t.name} (read-only, 30 min)`);
      window.location.href = '/tenant-dashboard';
    } catch (_e) {
      toast.error('Network error');
    } finally { setSwitchingTo(null); }
  };

  const filtered = q
    ? tenants.filter((t) => (
        (t.name || '').toLowerCase().includes(q.toLowerCase()) ||
        (t.email || '').toLowerCase().includes(q.toLowerCase()) ||
        (t.unit_number || '').toLowerCase().includes(q.toLowerCase())
      ))
    : tenants;

  return (
    <Card
      className="p-4 sm:p-5 mb-6 border border-zinc-300 bg-white"
      data-testid="view-as-tenant-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Eye className="h-5 w-5 text-[#C9A962]" />
        <h3 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          VIEW AS TENANT &mdash; Support &amp; Troubleshooting
        </h3>
        <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px]">
          <ShieldOff className="h-3 w-3 mr-1" /> Read-only session
        </Badge>
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        See your resident&apos;s app exactly as they see it. 30-minute read-only preview — password changes,
        payments, and financial actions are disabled while previewing.
      </p>

      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, or unit"
          className="pl-9"
          data-testid="view-as-tenant-search"
        />
      </div>

      {loading && <p className="text-sm text-zinc-500">Loading residents…</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-sm text-zinc-500 text-center py-4" data-testid="view-as-tenant-empty">
          {q ? 'No residents match that search.' : 'No residents on this property yet.'}
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="max-h-72 overflow-y-auto divide-y divide-zinc-200 border border-zinc-200 rounded">
          {filtered.map((t) => (
            <div
              key={t.user_id}
              className="flex items-center justify-between px-3 py-2 hover:bg-zinc-50"
              data-testid={`view-as-tenant-row-${t.user_id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                  <UserIcon className="h-4 w-4 text-zinc-500" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate flex items-center gap-2">
                    <span className="truncate">{t.name || t.email}</span>
                    <ResidentRewardBadge
                      userId={t.user_id}
                      size="compact"
                      testIdPrefix={`pm-resident-reward-${t.user_id}`}
                    />
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {t.unit_number ? `Unit ${t.unit_number} · ` : ''}{t.email}
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => impersonate(t)}
                disabled={switchingTo === t.user_id}
                className="bg-[#C9A962] hover:bg-[#b89651] text-black font-bold shrink-0"
                data-testid={`view-as-tenant-btn-${t.user_id}`}
              >
                <Eye className="h-3 w-3 mr-1" />
                {switchingTo === t.user_id ? 'Loading…' : 'View'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
